/* ============================================================
   Fence Works — weekly cash forecast math (Budget & Cash → Cash)
   Plain functions, no DOM and no store. business.tsx turns the pipeline
   (deposits, balances, job materials and labor) and the owner's
   schedules (settings.cash: bills, recurring, payroll, debt) into a flat
   list of dated moves; this file expands the schedules, buckets moves
   into Monday-start weeks and scores the result.

   Dates are local calendar days as "YYYY-MM-DD" strings — they compare
   correctly as strings and never drift across a DST change the way
   millisecond arithmetic does.
   ============================================================ */
import { csvRecords, normHeader, parseMoney, parseDateLoose } from "./csv";

const CP_DAY = 86400000;
const CADENCES = [
  { id: "weekly", label: "Weekly", perYear: 52 },
  { id: "biweekly", label: "Every 2 weeks", perYear: 26 },
  { id: "semimonthly", label: "Twice a month", perYear: 24 },
  { id: "monthly", label: "Monthly", perYear: 12 },
  { id: "quarterly", label: "Quarterly", perYear: 4 },
  { id: "annual", label: "Yearly", perYear: 1 },
];

function cpPad(n: number) { return String(n).padStart(2, "0"); }
function ymdOf(d: Date): string { return d.getFullYear() + "-" + cpPad(d.getMonth() + 1) + "-" + cpPad(d.getDate()); }
function ymdParts(s: any) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ""));
  return m ? { y: +m[1], m: +m[2] - 1, d: +m[3] } : null;
}
function addDaysYmd(s: string, n: number): string {
  const p = ymdParts(s);
  if (!p) return s;
  return ymdOf(new Date(p.y, p.m, p.d + n));
}
/* whole calendar days from a to b (b − a); Math.round absorbs a DST hour */
function daysBetween(a: string, b: string): number {
  const pa = ymdParts(a), pb = ymdParts(b);
  if (!pa || !pb) return 0;
  return Math.round((new Date(pb.y, pb.m, pb.d).getTime() - new Date(pa.y, pa.m, pa.d).getTime()) / CP_DAY);
}
/* the Monday on or before a date */
function mondayOf(s: string): string {
  const p = ymdParts(s);
  if (!p) return s;
  const d = new Date(p.y, p.m, p.d);
  return addDaysYmd(s, -((d.getDay() + 6) % 7));
}
/* ISO-8601 week number (weeks start Monday; week 1 holds the year's first Thursday) */
function isoWeek(s: string): number {
  const p = ymdParts(s);
  if (!p) return 0;
  const d = new Date(Date.UTC(p.y, p.m, p.d));
  const dow = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dow);
  const y0 = Date.UTC(d.getUTCFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - y0) / CP_DAY + 1) / 7);
}
function monthDay(y: number, m: number, day: number): string {
  const last = new Date(y, m + 1, 0).getDate();
  const dt = new Date(y, m, Math.min(day, last));
  return ymdOf(dt);
}

/* Every date a schedule lands on in [from, to). `next` is the next date it's
   due; occurrences before `from` are treated as already paid (they're in
   today's bank balance) and skipped. Month-based cadences keep the day they
   started on — a bill due the 31st is due Feb 28 and then Mar 31 again, not
   the 28th forever. Twice-a-month pairs the start day with the day 15 later
   (the 1st → 1st & 16th; the 15th → 15th & month end). */
function expandCadence(next: any, cadence: any, from: string, to: string): string[] {
  const p = ymdParts(next);
  if (!p || !(from < to)) return [];
  const out: string[] = [];
  const push = (d: string) => { if (d >= from && d < to && d >= next) out.push(d); };
  const c = String(cadence || "monthly");
  if (c === "once") { push(next); return out; }
  if (c === "weekly" || c === "biweekly") {
    const step = c === "weekly" ? 7 : 14;
    let k = next < from ? Math.floor(daysBetween(next, from) / step) : 0;
    for (let guard = 0; guard < 800; guard++, k++) {
      const d = ymdOf(new Date(p.y, p.m, p.d + k * step));
      if (d >= to) break;
      push(d);
    }
    return out;
  }
  if (c === "semimonthly") {
    const a = p.d <= 15 ? p.d : p.d - 15;
    const b = a + 15;
    for (let k = 0; k < 800; k++) {
      const y = p.y + Math.floor((p.m + k) / 12), m = (p.m + k) % 12;
      const first = monthDay(y, m, a);
      if (first >= to) break;
      push(first);
      push(b >= 30 ? monthDay(y, m, 31) : monthDay(y, m, b));
    }
    return out;
  }
  const step = c === "quarterly" ? 3 : c === "annual" ? 12 : 1;
  for (let k = 0; k < 800; k++) {
    const mm = p.m + k * step;
    const d = monthDay(p.y + Math.floor(mm / 12), mm % 12, p.d);
    if (d >= to) break;
    push(d);
  }
  return out;
}

/* what a schedule costs in an average month */
function monthlyEquivalent(amount: any, cadence: any): number {
  const c = CADENCES.find((x) => x.id === cadence) || CADENCES[3];
  return ((Number(amount) || 0) * c.perYear) / 12;
}

/* settings.cash schedules → dated outflow moves inside [from, to).
   Unpaid bills past due land on `from` (you owe them now), flagged overdue. */
function scheduleMoves(cash: any, from: string, to: string) {
  const c = cash || {};
  const moves: any[] = [];
  (Array.isArray(c.bills) ? c.bills : []).forEach((b: any) => {
    const amt = Number(b && b.amount) || 0;
    if (!b || b.paid || !(amt > 0)) return;
    const due = ymdParts(b.due) ? b.due : from;
    if (due >= to) return;
    moves.push({ date: due < from ? from : due, dir: "out", amount: amt, kind: "bill", label: (b.vendor || "Bill") + (due < from ? " · overdue" : ""), overdue: due < from });
  });
  const expand = (list: any, kind: string, labelOf: (x: any) => string, amtOf: (x: any) => number) => {
    (Array.isArray(list) ? list : []).forEach((x: any) => {
      const amt = amtOf(x);
      if (!x || !(amt > 0)) return;
      expandCadence(x.next, x.cadence, from, to).forEach((d) => moves.push({ date: d, dir: "out", amount: amt, kind, label: labelOf(x) }));
    });
  };
  expand(c.recurring, "recurring", (x) => x.name || x.category || "Recurring", (x) => Number(x.amount) || 0);
  expand(c.payroll, "payroll", (x) => x.name || "Payroll", (x) => Number(x.amount) || 0);
  expand(c.debt, "debt", (x) => (x.lender || "Loan") + " payment", (x) => Number(x.payment) || 0);
  return moves;
}

/* Bucket moves into `weeks` Monday-start weeks from `weekStart` and run the
   balance forward. Anything dated before the first week (overdue) lands in
   it; anything past the horizon is dropped. `inflowMult` is the scenario —
   it scales EXPECTED customer money only (a move with expected: false, a
   payment already recorded, is a fact and stays as-is); outflows never scale.
   Status: below the floor → below; within 20% above it → near; else healthy. */
function buildForecast(o: any) {
  const n = Math.max(1, Math.min(104, Math.round(Number(o.weeks) || 13)));
  const floor = Number(o.floor) || 0;
  const mult = o.inflowMult == null ? 1 : Math.max(0, Number(o.inflowMult) || 0);
  const weeks: any[] = Array.from({ length: n }, (_, i) => {
    const start = addDaysYmd(o.weekStart, i * 7);
    return { i, start, end: addDaysYmd(start, 6), cw: isoWeek(start), inflow: 0, outflow: 0, net: 0, ending: 0, moves: [] as any[], payroll: false, status: "healthy" };
  });
  (o.moves || []).forEach((mv: any) => {
    const amt0 = Number(mv && mv.amount) || 0;
    if (!(amt0 > 0) || !ymdParts(mv.date)) return;
    const i = Math.max(0, Math.floor(daysBetween(o.weekStart, mv.date) / 7));
    if (i >= n) return;
    const w = weeks[i];
    if (mv.dir === "in") {
      const amt = mv.expected === false ? amt0 : amt0 * mult;
      w.inflow += amt; w.moves.push({ ...mv, amount: amt });
    } else {
      w.outflow += amt0; w.moves.push({ ...mv });
      if (mv.kind === "payroll") w.payroll = true;
    }
  });
  let run = Number(o.start) || 0;
  weeks.forEach((w) => {
    w.net = w.inflow - w.outflow;
    run += w.net;
    w.ending = run;
    w.status = run < floor ? "below" : run < floor * 1.2 ? "near" : "healthy";
    w.moves.sort((a: any, b: any) => (a.dir === b.dir ? b.amount - a.amount : a.dir === "in" ? -1 : 1));
  });
  return weeks;
}

/* The headline numbers.
     ending   — cash at the end of the last week in range
     above    — weeks that end at or above the floor (of `total`)
     runway   — cash today ÷ average weekly net burn, where burn is outflows
                minus inflows averaged over the first 4 forecast weeks (fewer
                if the range is shorter). Only when that burn is > 0; when
                money coming in covers money going out, runway is null
                ("not burning"). Cash at or below $0 → 0 weeks.
     breach   — index of the first week that ends below the floor, or null */
function cashKpis(weeks: any[], start: number, floor: number) {
  const ws = weeks || [];
  const f = Number(floor) || 0;
  const k = Math.min(4, ws.length);
  const burn = k ? ws.slice(0, k).reduce((a, w) => a + (w.outflow - w.inflow), 0) / k : 0;
  const cash = Number(start) || 0;
  const runway = burn > 0 ? (cash <= 0 ? 0 : cash / burn) : null;
  const bi = ws.findIndex((w) => w.ending < f);
  return {
    ending: ws.length ? ws[ws.length - 1].ending : cash,
    above: ws.filter((w) => w.ending >= f).length,
    total: ws.length,
    burn, runway,
    breach: bi >= 0 ? bi : null,
    low: ws.reduce((m, w) => Math.min(m, w.ending), ws.length ? Infinity : cash),
  };
}

/* ---- AP / recurring CSV upload ------------------------------------------ */
const AP_COLS: Record<string, string[]> = {
  name: ["vendor", "vendorname", "payee", "payeename", "supplier", "name", "description", "expense", "item", "memo"],
  amount: ["amount", "openbalance", "balance", "amountdue", "balancedue", "total", "payment", "cost"],
  date: ["duedate", "due", "nextdate", "nextdue", "nextduedate", "nextpayment", "date", "billdate", "startdate"],
  cadence: ["cadence", "frequency", "freq", "every", "schedule", "interval", "recurs"],
  category: ["category", "type", "account", "expenseaccount"],
};
/* header row → { name, amount, date, cadence, category } column names (or null) */
function mapApHeaders(headers: string[]) {
  const norm = headers.map((h) => normHeader(h));
  const out: any = {};
  Object.keys(AP_COLS).forEach((k) => {
    let hit: string | null = null;
    for (const want of AP_COLS[k]) { const i = norm.indexOf(want); if (i >= 0) { hit = headers[i]; break; } }
    out[k] = hit;
  });
  return out;
}
/* "every 2 weeks" / "Bi-weekly" / "Twice a month" / "yearly" → a cadence id, or null */
function parseCadence(v: any): string | null {
  const s = String(v == null ? "" : v).toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!s) return null;
  if (/^(weekly|week|everyweek|1w|wk)$/.test(s)) return "weekly";
  if (/^(biweekly|every2weeks|everyotherweek|fortnightly|2w|every2wks)$/.test(s)) return "biweekly";
  if (/^(semimonthly|twiceamonth|twicemonthly|1stand15th|15thandlast|2xmonth)$/.test(s)) return "semimonthly";
  if (/^(monthly|month|everymonth|1m|mo)$/.test(s)) return "monthly";
  if (/^(quarterly|quarter|every3months|3m|qtr)$/.test(s)) return "quarterly";
  if (/^(annual|annually|yearly|year|everyyear|12m|yr)$/.test(s)) return "annual";
  return null;
}
/* csvRecords() output → preview rows for bills or recurring costs. Each row
   says what it will add, or why it can't: a row with errors is shown and
   skipped, never half-imported. */
function apRowsFromRecords(rec: any, kind: string) {
  const headers: string[] = (rec && rec.headers) || [];
  const cols = mapApHeaders(headers);
  const missing: string[] = [];
  if (!cols.name) missing.push(kind === "bills" ? "vendor" : "name");
  if (!cols.amount) missing.push("amount");
  if (!cols.date) missing.push(kind === "bills" ? "due date" : "next date");
  const rows = ((rec && rec.rows) || []).map((r: any, i: number) => {
    const errors: string[] = [];
    const name = cols.name ? String(r[cols.name] || "").trim() : "";
    const amount = cols.amount ? parseMoney(r[cols.amount]) : null;
    const date = cols.date ? parseDateLoose(r[cols.date]) : null;
    if (!name) errors.push(kind === "bills" ? "no vendor" : "no name");
    if (amount == null) errors.push("amount isn't a number");
    else if (amount <= 0) errors.push("amount is zero or a credit");
    if (!date) errors.push(cols.date && String(r[cols.date] || "").trim() ? "date not recognized" : "no date");
    const data: any = kind === "bills"
      ? { vendor: name, amount: amount != null ? Math.round(amount * 100) / 100 : null, due: date }
      : { name, amount: amount != null ? Math.round(amount * 100) / 100 : null, next: date, cadence: "monthly", category: cols.category ? String(r[cols.category] || "").trim() : "" };
    if (kind !== "bills" && cols.cadence) {
      const raw = String(r[cols.cadence] || "").trim();
      const cad = parseCadence(raw);
      if (cad) data.cadence = cad;
      else if (raw) errors.push("cadence \"" + raw.slice(0, 20) + "\" not recognized");
    }
    return { line: i + 2, ok: errors.length === 0, errors, data };
  });
  return { cols, missing, rows };
}
function parseApCsv(text: string, kind: string) { return apRowsFromRecords(csvRecords(text), kind); }

/* Fold QuickBooks open bills into settings.cash.bills, keyed by the QBO id:
   a new id is added, a known one takes QuickBooks' current balance and due
   date, and a bill we imported earlier that is no longer open in QuickBooks
   (paid there) is marked paid here. Bills typed in by hand are untouched. */
function mergeQboBills(existing: any[], open: any[], stamp?: number) {
  const list = (existing || []).map((b: any) => ({ ...b }));
  const openIds = new Set((open || []).map((b: any) => String(b.id)));
  let added = 0, updated = 0, closed = 0;
  (open || []).forEach((ob: any) => {
    const amt = Math.round((Number(ob.amount) || 0) * 100) / 100;
    if (!(amt > 0)) return;
    const due = ymdParts(ob.dueDate) ? ob.dueDate : null;
    const cur = list.find((b: any) => b.qboId && String(b.qboId) === String(ob.id));
    if (cur) {
      if (cur.amount !== amt || (due && cur.due !== due) || cur.paid) { cur.amount = amt; if (due) cur.due = due; cur.paid = false; updated++; }
    } else {
      list.push({ id: "bill-qbo-" + ob.id, qboId: String(ob.id), vendor: ob.vendor || "QuickBooks bill", amount: amt, due, paid: false, source: "quickbooks", addedAt: stamp || 0 });
      added++;
    }
  });
  list.forEach((b: any) => { if (b.qboId && !openIds.has(String(b.qboId)) && !b.paid) { b.paid = true; closed++; } });
  return { bills: list, added, updated, closed };
}

export {
  CADENCES, AP_COLS, ymdOf, ymdParts, addDaysYmd, daysBetween, mondayOf, isoWeek, monthDay, expandCadence, monthlyEquivalent,
  scheduleMoves, buildForecast, cashKpis, mapApHeaders, parseCadence, apRowsFromRecords, parseApCsv, mergeQboBills,
};
